import React, { createContext, ReactChild, ReactNode, useContext, useState } from "react"
import { Row } from "react-bootstrap"
import AcButton from "~/components/atoms/AcButton"
import AcModal, { ModalInnerTitle } from "~/components/atoms/AcModal"
import AcLoading from "~/components/atoms/AcLoading"
import { useDispatch } from "react-redux"
import { Socket } from "~/utils/socket"
import { nl2br } from "~/utils/functions"
import { useRouter } from "next/router"

export interface IGlobalContext {
  title: string
  socket: Socket
  setTitle: (title: string) => void
  showDialog: (
    title: string,
    message: string | ReactChild | ReactChild[],
    buttonName?: string,
    disables?: boolean
  ) => Promise<boolean>
  loading: (message: string | boolean) => void
}

export const GlobalContext = createContext<IGlobalContext>(null!)

interface GlobalProviderProps {
  socket: Socket
  // onError: (code: number, error: string) => void
  children: ReactNode
}

export function GlobalProvider(props: GlobalProviderProps) {
  const { children, socket } = props
  const dispatch = useDispatch()
  const router = useRouter()

  const [globalTitle, setGlobalTitle] = useState("")
  const [globalErrorDialog, setGlobalErrorDialog] = useState({
    show: false,
    title: "",
    message: "",
    buttonName: "",
    resolve: null,
    disables: false,
  })

  const [globalLoadingMessage, setGlobalLoadingMessage] = useState<boolean | string>(false)

  const setTitle = (title: string) => {
    setGlobalTitle(title)
  }

  const loading = (message: string | boolean) => {
    setGlobalLoadingMessage(message)
  }

  // useEffect(() => {
  //   dispatch(fetchConfig())
  // }, [])

  const showDialog = (title: string, message: string, buttonName?: string, disables?: boolean) => {
    return new Promise((resolve) => {
      setGlobalLoadingMessage(false)
      setGlobalErrorDialog({
        show: true,
        title,
        message,
        buttonName,
        resolve,
        disables,
      })
    })
  }

  return (
    <GlobalContext.Provider
      value={{
        socket,
        title: globalTitle,
        setTitle,
        showDialog: showDialog as never,
        loading,
      }}
    >
      <>
        {globalLoadingMessage !== false && (
          <AcLoading
            isOverlay={true}
            message={globalLoadingMessage === true ? null : globalLoadingMessage}
          />
        )}
        {children}
        {globalErrorDialog.show && (
          <AcModal
            size={"lg"}
            show={globalErrorDialog.show}
            onHide={async () => {
              await globalErrorDialog?.resolve?.call(this, false)
              setGlobalErrorDialog({ show: false } as never)
            }}
            title={globalErrorDialog.title}
            disabled={globalErrorDialog.disables}
          >
            <React.Fragment key={"body"}>
              {typeof globalErrorDialog.message !== "string" &&
              !Array.isArray(globalErrorDialog.message) ? (
                <>{globalErrorDialog.message}</>
              ) : (
                <ModalInnerTitle>{nl2br(globalErrorDialog.message)}</ModalInnerTitle>
              )}
              {globalErrorDialog.buttonName !== null && (
                <Row className={"buttons"}>
                  <AcButton
                    className={"btn-common"}
                    onClick={async () => {
                      await globalErrorDialog?.resolve?.call(this, true)
                      setGlobalErrorDialog({ show: false } as never)
                    }}
                  >
                    {globalErrorDialog.buttonName}
                  </AcButton>
                </Row>
              )}
            </React.Fragment>
          </AcModal>
        )}
      </>
    </GlobalContext.Provider>
  )
}

export function useGlobal() {
  const context = useContext(GlobalContext)
  if (!context) {
    throw new Error("useGlobalContext must be used within a GlobalProvider")
  }
  return context
}
